import React, { useState } from "react";
import PropTypes from "prop-types";
import { stringToColor } from "../../components/CalendarCalendar/calendarHelpers";
import { addTag, removeTag } from "./messageCenterHelpers";
import styles from "./TagChips.module.css";

/**
 * Tags livres por conversa (F2.5).
 *
 * Input livre: digitar + Enter (ou vírgula) adiciona; "×" no chip remove;
 * Backspace com o campo vazio remove a última. Cada mudança computa o array
 * COMPLETO de tags e chama `onChange(next)` — o pai faz o
 * `PATCH /tenants/sessions/:id/tags { tags }` (o backend substitui o array
 * inteiro) com merge otimista. Sem vocabulário controlado.
 *
 * A cor do chip vem de `stringToColor(tag)` para leitura rápida, mas entra só
 * num ponto (swatch): o texto fica em token neutro sobre superfície clara, o que
 * garante contraste WCAG AA independentemente da cor arbitrária — mesmo padrão
 * do `DepartmentBadge` (F2.4).
 */
export default function TagChips({ tags, onChange, busy = false }) {
  const [input, setInput] = useState("");

  const commit = () => {
    const next = addTag(tags, input);
    setInput("");
    // Só dispara quando algo mudou de fato (evita PATCH em tag vazia/duplicada).
    if (next.length !== tags.length) onChange(next);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit();
    } else if (e.key === "Backspace" && !input && tags.length) {
      e.preventDefault();
      onChange(removeTag(tags, tags[tags.length - 1]));
    }
  };

  return (
    <div className={styles.wrap}>
      <span className={styles.label} id="tag-chips-label">
        Tags
      </span>
      <div
        className={styles.chips}
        role="list"
        aria-labelledby="tag-chips-label"
      >
        {tags.map((tag) => (
          <span key={tag} role="listitem" className={styles.chip}>
            <span
              className={styles.dot}
              style={{ background: stringToColor(tag) }}
              aria-hidden="true"
            />
            <span className={styles.chipLabel}>{tag}</span>
            <button
              type="button"
              className={styles.remove}
              onClick={() => onChange(removeTag(tags, tag))}
              disabled={busy}
              aria-label={`Remover tag ${tag}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          className={styles.input}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commit}
          disabled={busy}
          placeholder={tags.length ? "Adicionar…" : "Adicionar tag (Enter)"}
          aria-label="Adicionar tag"
        />
      </div>
    </div>
  );
}

TagChips.propTypes = {
  tags: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};
